<template>
  <form @submit.prevent="handleSubmit" class="side-wrapper side-wrapper-flex">
    <!-- Header -->
    <div class="side-header">
      <div class="side-wrapper-subtitle">{{ generalStore.viewHeader }}</div>
      <h1 v-if="form.id">{{ $gettext("Edit guest") }} '{{ form.first_name }}'</h1>
      <h1 v-else>{{ $gettext("Create guest") }}</h1>
    </div>

    <!-- Body -->
    <div class="side-body">
      <!-- Group -->
      <NiceDropdown :title="$gettext('Group')" v-model="form.group" :searchFunction="searchGroups" valueName="name" :required="true" />

      <!-- First name -->
      <NiceInput :title="$gettext('First name')" v-model="form.first_name" type="text" placeholder="Janez" :required="true" />

      <!-- Last name -->
      <NiceInput :title="$gettext('Last name')" v-model="form.last_name" type="text" placeholder="Novak" :required="true" />

      <!-- Partner -->
      <NiceDropdown
        :title="$gettext('Partner')"
        v-model="form.partner"
        :searchFunction="searchPartners"
        valueName="name"
        nullable
        :required="form.group && form.group.require_partner"
        v-if="modules.PARTNERS"
      />

      <!-- Company name -->
      <NiceInput
        :title="$gettext('Company name')"
        v-model="form.company_name"
        type="text"
        placeholder="Olaii d.o.o."
        :required="form.group && form.group.require_company"
      />

      <!-- Email -->
      <NiceInput
        :title="$gettext('Email')"
        v-model="form.email"
        type="email"
        placeholder="janez@olaii.com"
        :required="form.group && form.group.require_email"
      />

      <!-- Phone number -->
      <NiceInput
        :title="$gettext('Phone number')"
        v-model="form.phone_number"
        type="text"
        placeholder="000 000 000"
        :required="form.group && form.group.require_phone_number"
      />

      <!-- Plus one count -->
      <NiceInput
        :title="$gettext('Plus one count')"
        v-model="form.plus_one_count"
        :max="form.group.plus_one_limit_per_guest"
        :min="0"
        type="number"
        :placeholder="form.group.plus_one_limit_per_guest"
        v-if="form.group && form.group.allow_plus_one && modules.PLUS_ONES"
      />

      <!-- Photo -->
      <NiceUpload
        :title="$gettext('Photo')"
        v-model="form.photo"
        accept="image/png, image/jpeg"
        @change="onFileChange"
        :required="form.group && form.group.require_photo"
        :loading="loading"
      />

      <!-- Open camera -->
      <NiceButton type="default" icon="icon-camera" @click="openCamera">
        {{ $gettext("Open camera") }}
      </NiceButton>

      <!-- PDF Viewier -->
      <CameraModal @success="handleCameraCapture"></CameraModal>

      <!-- Actions -->
      <div v-if="form.id" class="mt-3">
        <!-- Deactivate -->
        <NiceButton v-if="!form.deactivated_by" :disabled="!permissions.can_deactivate_guests" type="danger" icon="icon-x" @click="deactivate">
          {{ $gettext("Deactivate") }}
        </NiceButton>

        <!-- Reactivate -->
        <NiceButton
          v-if="form.deactivated_by"
          :disabled="!permissions.can_reactivate_guests"
          type="danger"
          icon="icon-rotate-ccw"
          @click="reactivate"
        >
          {{ $gettext("Reactivate") }}
        </NiceButton>

        <NiceSlot v-if="form.deactivated_by" :title="$gettext('Deactivated by')" class="mt-4">
          {{ form.deactivated_by.first_name || "-" }} {{ form.deactivated_by.last_name || "-" }} ({{ form.deactivated_by.email || "-" }})
        </NiceSlot>
      </div>

      <!-- <pre>{{ form }}</pre> -->
    </div>

    <!-- Footer -->
    <div class="side-footer">
      <!-- <NiceActions @cancel="cancel" :loading="loading" :showDelete="!!form.id" @delete="handleDelete" /> -->
      <NiceActions @cancel="cancel" :loading="loading" />
    </div>
  </form>
</template>

<script setup>
import { defineProps, onMounted, defineEmits, inject, ref } from "vue";

import { useGettext } from "vue3-gettext";
const { $gettext } = useGettext();

import { useRouter } from "vue-router";
const router = useRouter();

import { useGeneralStore } from "@/store/general.store";
const generalStore = useGeneralStore();
const permissions = ref(generalStore.permissions);
const modules = ref(generalStore.modules);

import ApiService from "@/services/api/api.service";
import CommonService from "@/services/common.service";
import ErrorService from "@/services/error.service";
import { clone } from "@/plugins/query";
import CameraModal from "../modals/CameraModal.vue";

const emit = defineEmits(["cancel", "success"]);
const props = defineProps({
  data: {
    type: Object,
    required: false,
  },
});
const form = ref({
  group: null,
  partner: null,
  first_name: null,
  last_name: null,
  company_name: null,
  email: null,
  phone_number: null,
  photo: null,
  plus_one_count: 0,
});
const $nice = inject("nice");
const loading = ref(false);
const organizationId = router.currentRoute.value.params.organizationId;
const eventId = router.currentRoute.value.params.eventId;

async function handleSubmit() {
  loading.value = true;
  try {
    if (form.value.id) {
      form.value = await ApiService.guests.update(organizationId, eventId, form.value);
      CommonService.success($gettext("Guest was updated"));
    } else {
      form.value = await ApiService.guests.create(organizationId, eventId, form.value);
      CommonService.success($gettext("Guest was created"));
    }
    emit("success");
  } catch (error) {
    ErrorService.showError(error);
  } finally {
    loading.value = false;
  }
}

// async function handleDelete() {
//   loading.value = true;
//   try {
//     form.value = await ApiService.guests.delete(organizationId, eventId, form.value.id);
//     CommonService.success($gettext("Guest was deleted"));
//     emit("success");
//   } catch (error) {
//     ErrorService.showError(error);
//   } finally {
//     loading.value = false;
//   }
// }

async function deactivate() {
  loading.value = true;
  try {
    form.value = await ApiService.guests.deactivate(organizationId, eventId, form.value.id);
    CommonService.success($gettext("Guest was deactivated"));
    emit("success");
  } catch (error) {
    ErrorService.showError(error);
  } finally {
    loading.value = false;
  }
}

async function reactivate() {
  loading.value = true;
  try {
    form.value = await ApiService.guests.activate(organizationId, eventId, form.value.id);
    CommonService.success($gettext("Guest was reactivated"));
    emit("success");
  } catch (error) {
    ErrorService.showError(error);
  } finally {
    loading.value = false;
  }
}

function cancel() {
  emit("cancel");
}

async function searchGroups(keyword) {
  return ApiService.groups.getAll(organizationId, eventId, { search: keyword });
}

async function searchPartners(keyword) {
  return ApiService.partners.getAll(organizationId, eventId, { search: keyword });
}

async function openCamera() {
  $nice.modal("camera-modal", true);
}

async function onFileChange(file) {
  if (!file) return (form.value.photo = null);
  if (!file[0]) return (form.value.photo = null);
  if (!["image/jpeg", "image/png"].includes(file[0].type)) {
    CommonService.error($gettext("Wrong file format. Only .jpg and .png files are allowed."));
    return (form.value.photo = null);
  }

  loading.value = true;
  try {
    const response = await ApiService.storage.uploadPhoto(file[0], CommonService.photoCompressionTypes.COVER);
    form.value.photo = response.file;
  } catch (error) {
    ErrorService.showError(error);
    form.value.photo = null;
  } finally {
    loading.value = false;
  }
}

function handleCameraCapture(blob) {
  if (!blob) return;
  const file = new File([blob], "capture.jpg", { type: "image/jpeg" });
  const dataTransfer = new DataTransfer();
  dataTransfer.items.add(file);

  form.value.photo = dataTransfer.files;
  onFileChange(dataTransfer.files);
}

onMounted(async () => {
  const partnersId = router.currentRoute.value.query.id;
  if (partnersId == "new") return;
  if (props.data) return (form.value = clone(props.data));

  try {
    form.value = await ApiService.guests.get(organizationId, eventId, partnersId);
  } catch (e) {
    emit("cancel");
  }
});
</script>
